import { useState, useCallback, useEffect, useRef } from 'react'
import { useLangGraphStream } from '@/hooks/useLangGraphStream'
import { useAuthStore } from '@/state/authStore'

export interface AgentStep {
  tool: string
  args?: Record<string, unknown>
}

export interface ChatMessage {
  role: 'user' | 'agent'
  content: string
  timestamp: string
  steps?: AgentStep[]
}

export interface Conversation {
  id: string
  preview: string
  timestamp: string
}

export function useAgentChat(clientId: string) {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [conversationsLoading, setConversationsLoading] = useState(true)
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)

  // Track whether the current conversation is a pending new chat (not yet saved to server)
  const isPendingNewRef = useRef(false)

  const loadConversations = useCallback(async () => {
    try {
      const token = await useAuthStore.getState().getToken()
      const resp = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/clients/${clientId}/agent-chat/conversations`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (resp.ok) {
        const data = await resp.json() as { conversations: Conversation[] }
        setConversations(data.conversations)
        return data.conversations
      }
    } catch {
      // non-fatal
    }
    return []
  }, [clientId])

  const loadMessages = useCallback(async (conversationId: string) => {
    setMessagesLoading(true)
    setMessages([])
    try {
      const token = await useAuthStore.getState().getToken()
      const resp = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/clients/${clientId}/agent-chat/history?conversation_id=${encodeURIComponent(conversationId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (resp.ok) {
        const data = await resp.json() as { messages: Array<{ role: string; content: string; timestamp: string }> }
        setMessages(data.messages.map((m) => ({
          role: (m.role === 'assistant' ? 'agent' : m.role) as 'user' | 'agent',
          content: m.content,
          timestamp: m.timestamp,
        })))
      }
    } catch {
      // non-fatal
    } finally {
      setMessagesLoading(false)
    }
  }, [clientId])

  // On mount: load conversations then auto-select the most recent one
  useEffect(() => {
    void (async () => {
      setConversationsLoading(true)
      const convos = await loadConversations()
      setConversationsLoading(false)
      if (convos.length > 0) {
        isPendingNewRef.current = false
        setCurrentConversationId(convos[0].id)
      }
      // If no conversations, currentConversationId stays null — sendMessage will create one
    })()
  }, [clientId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load messages whenever the selected conversation changes (skip pending-new)
  useEffect(() => {
    if (currentConversationId && !isPendingNewRef.current) {
      void loadMessages(currentConversationId)
    }
  }, [currentConversationId, loadMessages])

  const startNewChat = useCallback(() => {
    const newId = crypto.randomUUID()
    isPendingNewRef.current = true
    setCurrentConversationId(newId)
    setMessages([])
  }, [])

  const selectConversation = useCallback((id: string) => {
    isPendingNewRef.current = false
    setCurrentConversationId(id)
  }, [])

  const { stream, isStreaming } = useLangGraphStream({
    onEvent: (_type, data) => {
      try {
        const ev = JSON.parse(data) as { type: string; response?: string; conversation_id?: string; steps?: AgentStep[] }
        if (ev.type === 'message' && ev.response) {
          setMessages((prev) => [
            ...prev,
            { role: 'agent', content: ev.response as string, timestamp: new Date().toISOString(), steps: ev.steps },
          ])
          // After the first real response, refresh conversation list so the new chat appears
          void loadConversations().then((convos) => setConversations(convos))
          isPendingNewRef.current = false
        }
      } catch {
        // skip malformed events
      }
    },
    onDone: () => {},
    onError: (message) => {
      console.error('[useAgentChat] stream error:', message)
    },
  })

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim()) return

      // If no conversation is active yet, create one now
      const convId = currentConversationId ?? crypto.randomUUID()
      if (!currentConversationId) {
        isPendingNewRef.current = true
        setCurrentConversationId(convId)
      }

      const userMsg: ChatMessage = {
        role: 'user',
        content: text,
        timestamp: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, userMsg])

      // Optimistically add to conversation list if this is a new chat
      if (!conversations.find((c) => c.id === convId)) {
        setConversations((prev) => [
          { id: convId, preview: text.slice(0, 60), timestamp: new Date().toISOString() },
          ...prev,
        ])
      }

      await stream(
        `${import.meta.env.VITE_API_BASE_URL}/api/clients/${clientId}/agent-chat`,
        { message: text, conversation_id: convId },
      )
    },
    [clientId, currentConversationId, conversations, stream],
  )

  return {
    conversations,
    conversationsLoading,
    messages,
    messagesLoading,
    loading: isStreaming,
    currentConversationId,
    sendMessage,
    startNewChat,
    selectConversation,
  }
}
